import { useMemo } from 'react';
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';
import {
  addDays,
  isoWeekNumber,
  parseDayIso,
  startOfWeekIso,
  toIsoDate,
  toWeekday,
  weekBoundsIso,
  weekdaysOfWeek,
} from '../../../shared/lib/schedule';
import { DISPOSITION_VIEWS } from './useDispositionView';

/**
 * The technician a click in the week view asked the day board to scroll to. Written by `openDay`,
 * consumed and cleared by `DispositionBoardWidget`.
 */
export const FOCUS_TECHNICIAN_PARAM = 'focus-technician';

export interface DispositionWeek {
  /** Monday of the week on screen. */
  weekStartIso: string;
  /** The five columns, Montag … Freitag. */
  days: string[];
  /** Monday 00:00 → Friday 23:59:59.999, for `GET /schedule`. */
  from: string;
  to: string;
  /** So the grid can tint the column that is really today. */
  todayIso: string;
  isCurrentWeek: boolean;
  /** `KW 34 · 18.–22. August 2026`. */
  label: string;
  prev: () => void;
  next: () => void;
  goToToday: () => void;
  /**
   * Leave the week for the day board. Writes the day and flips the view back to `tag`; with a
   * technician it also asks the board to scroll that row into view.
   */
  openDay: (dayIso: string, technicianId?: number) => void;
}

const monthDayFormatter = new Intl.DateTimeFormat('de-DE', { day: 'numeric' });
const fullFormatter = new Intl.DateTimeFormat('de-DE', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const dayMonthFormatter = new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'long' });

/**
 * The week the board is showing — derived from the **same `?day=` anchor** the day view uses.
 *
 * One param for both views is what makes the toggle lossless: Tag → Woche shows the week containing
 * the day, Woche → Tag returns to that exact day, and the existing `routes.disposition.reschedule*`
 * deep links — which already write `?day=` — keep working without knowing this view exists.
 *
 * Weekdays only, like `useDispositionDay`: a weekend anchor resolves to the following Monday, so
 * `?day=` always names a day that is actually drawn.
 */
export function useDispositionWeek(): DispositionWeek {
  const todayIso = toIsoDate(new Date());

  const [rawDay, setDay] = useQueryState('day', parseAsString.withDefault(todayIso));
  // Same parser and options as `useDispositionView`, so leaving for the day board drops `view=`
  // from the URL entirely instead of leaving a redundant `view=tag` behind.
  const [, setView] = useQueryState(
    'view',
    parseAsStringLiteral(DISPOSITION_VIEWS).withDefault('tag').withOptions({
      history: 'replace',
      shallow: true,
      clearOnDefault: true,
    }),
  );
  const [, setFocusTechnician] = useQueryState(FOCUS_TECHNICIAN_PARAM, parseAsInteger);

  // Unlike the day view this does not write the correction back: the week is the same whichever day
  // of it the anchor names, so rewriting the URL here would only fight `useDispositionDay`, which
  // mounts alongside it on the same page.
  const anchorIso = useMemo(
    () => toWeekday(parseDayIso(rawDay) ? rawDay : todayIso),
    [rawDay, todayIso],
  );

  const days = useMemo(() => weekdaysOfWeek(anchorIso), [anchorIso]);
  const { from, to } = useMemo(() => weekBoundsIso(anchorIso), [anchorIso]);
  const weekStartIso = days[0];

  const label = useMemo(() => buildWeekLabel(days), [days]);

  return {
    weekStartIso,
    days,
    from,
    to,
    todayIso,
    isCurrentWeek: weekStartIso === startOfWeekIso(todayIso),
    label,
    prev: () => {
      void setDay(addDays(anchorIso, -7));
    },
    next: () => {
      void setDay(addDays(anchorIso, 7));
    },
    goToToday: () => {
      void setDay(toWeekday(todayIso));
    },
    openDay: (dayIso, technicianId) => {
      void setDay(toWeekday(dayIso));
      void setView(DISPOSITION_VIEWS[0]);
      void setFocusTechnician(technicianId ?? null);
    },
  };
}

/**
 * `KW 34 · 18.–22. August 2026`, collapsing the parts the two ends share — a week inside one month
 * repeats neither the month nor the year, one crossing a month keeps both.
 */
function buildWeekLabel(days: string[]): string {
  const start = parseDayIso(days[0]);
  const end = parseDayIso(days[days.length - 1]);
  if (!start || !end) return '';

  const kw = `KW ${isoWeekNumber(days[0])}`;

  if (start.getFullYear() !== end.getFullYear()) {
    return `${kw} · ${fullFormatter.format(start)} – ${fullFormatter.format(end)}`;
  }
  if (start.getMonth() !== end.getMonth()) {
    return `${kw} · ${dayMonthFormatter.format(start)} – ${fullFormatter.format(end)}`;
  }
  return `${kw} · ${monthDayFormatter.format(start)}.–${fullFormatter.format(end)}`;
}
